import types from '../types'
import { arrayMin, indexOf, intersect } from 'common/utils/arrayUtils'

const initialState = {
  /* ex:
  train: {},
  bus: {},
  plane: {}
  */
}

const searchResponsesReducer = (state = initialState, action) => {
  switch (action.type) {
    case types.RESPONSE_ADD:
      return addResponse(state, action.payload, action.mode, action.addBoth, action.isOutbound)
    case types.RESPONSE_REPLACE:
      return replaceResponse(state, action.response, action.modeId, action.isOutbound)
    case types.RESPONSE_REPLACE_ALL:
      return replaceEntireResponse(state, action.response, action.modeId)
    case types.RESPONSES_LOADING:
      return initialState
    case types.DETAILS_UPDATE:
      return handleDetailsUpdate(
        state,
        action.response,
        action.modeId,
        action.oItinId,
        action.iItinId,
      )
    case types.DETAILS_REAPPLY:
      return reapplyDetailsLinks(state, action.modeId, action.detailsLinks)
    default:
      return state
  }
}

// Returns a state with a response merged in
// Either adding both outbound and inbound (eg. when doing initial search)
//  (addBoth = true, isOutbound = null)
// Or just adding one direction (eg. when fetching more)
//  ("addBoth" = false and isOutbound is true/false)
const addResponse = (state, response, modeId, addBoth, isOutbound) => {
  const newResponse = { ...response }

  if (!addBoth && isOutbound) {
    newResponse.inboundItineraries = []
  } else if (!addBoth && !isOutbound) {
    newResponse.outboundItineraries = []
  }

  return {
    ...state,
    [modeId]: mergeResponses(state[modeId], newResponse),
  }
}

const replaceResponse = (state, response, modeId, isOutbound) => {
  const prunedResponse = removeItineraries(state[modeId], isOutbound)
  return {
    ...state,
    [modeId]: mergeResponses(prunedResponse, response),
  }
}

const replaceEntireResponse = (state, response, modeId) => {
  return {
    ...state,
    [modeId]: response,
  }
}

const handleDetailsUpdate = (state, detailsResponse, modeId, oItinId, iItinId) => {
  const resp = state[modeId]
  let oItinIndex = indexOf(resp.outboundItineraries, i => i.itineraryId === oItinId)
  let iItinIndex = indexOf(resp.inboundItineraries, i => i.itineraryId === iItinId)

  oItinIndex = oItinIndex < 0 ? null : oItinIndex
  iItinIndex = iItinIndex < 0 ? null : iItinIndex

  // If the itineraries the detailsLink applies to are not in this response, skip it
  if ((oItinId != null && oItinIndex == null) || (iItinId != null && iItinIndex == null)) {
    return { ...state }
  }

  return {
    ...state,
    [modeId]: updateResponse(resp, detailsResponse, oItinIndex, iItinIndex),
  }
}

const reapplyDetailsLinks = (state, modeId, detailsLinks) => {
  let newState = state
  Object.entries(detailsLinks).forEach(kvp => {
    const detailsCache = kvp[1]

    newState = handleDetailsUpdate(
      newState,
      detailsCache.response,
      modeId,
      detailsCache.oItinId,
      detailsCache.iItinId
    )
  })

  return newState
}

const removeItineraries = (response, isOutbound) => {
  // Note: When filtering isn't done by the view we won't need to blow away these
  // itineraries and details links
  const responseCopy = { ...response }
  if (isOutbound) {
    // Blow away old outbound itineraries
    responseCopy.outboundItineraries = []
    responseCopy.detailsLinks = responseCopy.detailsLinks.filter(
      detailsLink => detailsLink.outboundItinerary != null
    )
  } else {
    // Blow away old inbound itineraries
    responseCopy.inboundItineraries = []
    responseCopy.detailsLinks = responseCopy.detailsLinks.filter(
      detailsLink => detailsLink.inboundItinerary != null
    )
  }
  // Blow away all leg pair fares
  responseCopy.fares = responseCopy.fares.filter(
    fare => !(fare.legPairs && fare.legPairs.length > 0)
  )

  return responseCopy
}

// r1 and r2 are both deserialized
const mergeResponses = (r1, r2) => {
  if (!r1 || !r2) {
    return r1 || r2
  }

  // Existing number of things so we can increment r2's indexes
  const nFacilities = r1.facilities.length
  const nCarriers = r1.carriers.length
  const nVehicles = r1.vehicles.length
  const nPlaces = r1.places.length
  const nPaths = r1.paths.length
  const nLines = r1.lines.length
  const nHops = r1.hops.length
  const nLayovers = r1.layovers.length
  const nLegs = r1.legs.length
  const nOutboundItins = r1.outboundItineraries.length
  const nInboundItins = r1.inboundItineraries.length
  const nBookingData = r1.bookingData.length

  // Local functions to increase an index: handles optional indexes
  const increaseIndex = (idx, inc) => (idx || idx === 0 ? idx + inc : idx)
  const increaseIndexes = (idxs, inc) =>
    (idxs && idxs.length ? idxs.map(i => increaseIndex(i, inc)) : idxs)

  // Create the merged response to return by increasing r2's indexes
  // Note that this doesn't do any de-duping (and probably shouldn't)
  const merged = { ...r1 }
  merged.facilities.push(...r2.facilities)
  merged.carriers.push(...r2.carriers)
  merged.vehicles.push(...r2.vehicles.map(vehicle =>
    ({
      ...vehicle,
      facilities: increaseIndexes(vehicle.facilities, nFacilities),
    }))
  )
  merged.places.push(...r2.places)
  merged.paths.push(...r2.paths)
  merged.lines.push(...r2.lines.map(line =>
    ({
      ...line,
      places: increaseIndexes(line.places, nPlaces),
      path: increaseIndex(line.path, nPaths),
    }))
  )
  merged.hops.push(...r2.hops.map(hop =>
    ({
      ...hop,
      line: increaseIndex(hop.line, nLines),
      marketingCarrier: increaseIndex(hop.marketingCarrier, nCarriers),
      operatingCarrier: increaseIndex(hop.operatingCarrier, nCarriers),
      vehicle: increaseIndex(hop.vehicle, nVehicles),
    }))
  )
  merged.layovers.push(...r2.layovers)
  merged.legs.push(...r2.legs.map(leg =>
    ({
      ...leg,
      hops: increaseIndexes(leg.hops, nHops),
      layovers: increaseIndexes(leg.layovers, nLayovers),
    }))
  )
  merged.outboundItineraries.push(...r2.outboundItineraries.map(itin =>
    ({
      ...itin,
      legs: increaseIndexes(itin.legs, nLegs),
      layovers: increaseIndexes(itin.layovers, nLayovers),
      bookingDataIndex: increaseIndex(itin.bookingDataIndex, nBookingData),
    }))
  )
  merged.inboundItineraries.push(...r2.inboundItineraries.map(itin =>
    ({
      ...itin,
      legs: increaseIndexes(itin.legs, nLegs),
      layovers: increaseIndexes(itin.layovers, nLayovers),
      bookingDataIndex: increaseIndex(itin.bookingDataIndex, nBookingData),
    }))
  )
  merged.detailsLinks.push(...r2.detailsLinks.map(detailsLink =>
    ({
      ...detailsLink,
      outboundItinerary: increaseIndex(detailsLink.outboundItinerary, nOutboundItins),
      inboundItinerary: increaseIndex(detailsLink.inboundItinerary, nInboundItins),
    }))
  )
  merged.fares.push(...r2.fares.map(fare =>
    ({
      ...fare,
      legs: increaseIndexes(fare.legs, nLegs),
      legPairs: fare.legPairs ? fare.legPairs.map(lp =>
        ({
          outboundLeg: increaseIndex(lp.outboundLeg, nLegs),
          inboundLeg: increaseIndex(lp.inboundLeg, nLegs),
        })) : fare.legPairs,
    }))
  )
  merged.bookingData.push(...r2.bookingData)

  return merged
}

// response and detailsResponse are both deserialized
const updateResponse = (response, detailsResponse, oItinIndex, iItinIndex) => {
  const indexMap = createIndexMap(response, detailsResponse, oItinIndex, iItinIndex)

  const responseCopy = { ...response }
  // Shallow copy stuff we plan on modifying
  responseCopy.outboundItineraries = [...responseCopy.outboundItineraries]
  responseCopy.inboundItineraries = [...responseCopy.inboundItineraries]
  responseCopy.legs = [...responseCopy.legs]
  responseCopy.hops = [...responseCopy.hops]
  responseCopy.layovers = [...responseCopy.layovers]
  responseCopy.fares = [...responseCopy.fares]

  // Update stuff. The update functions modify response in-place.
  updateItineraries(responseCopy, detailsResponse, indexMap)
  updateLegs(responseCopy, detailsResponse, indexMap)
  updateHops(responseCopy, detailsResponse, indexMap)
  updateFares(responseCopy, detailsResponse, indexMap)

  // Add stuff
  responseCopy.layovers = responseCopy.layovers.concat(detailsResponse.layovers)

  return responseCopy
}

const updateItineraries = (oldResponse, detailsResponse, indexMap) => {
  Object.keys(indexMap.outboundItineraries).forEach(detailsItinIndex => {
    const origItinIndex = indexMap.outboundItineraries[detailsItinIndex]
    const updatedItinerary = detailsResponse.outboundItineraries[detailsItinIndex]
    const nOrigLayovers = oldResponse.layovers.length

    oldResponse.outboundItineraries[origItinIndex] = {
      ...oldResponse.outboundItineraries[origItinIndex],
      layovers: increaseValues(updatedItinerary.layovers, nOrigLayovers),
    }
  })

  Object.keys(indexMap.inboundItineraries).forEach(detailsItinIndex => {
    const origItinIndex = indexMap.inboundItineraries[detailsItinIndex]
    const updatedItinerary = detailsResponse.inboundItineraries[detailsItinIndex]
    const nOrigLayovers = oldResponse.layovers.length

    oldResponse.inboundItineraries[origItinIndex] = {
      ...oldResponse.inboundItineraries[origItinIndex],
      layovers: increaseValues(updatedItinerary.layovers, nOrigLayovers),
    }
  })
}

const updateLegs = (oldResponse, detailsResponse, indexMap) => {
  Object.keys(indexMap.legs).forEach(detailsLegIndex => {
    const origLegIndex = indexMap.legs[detailsLegIndex]
    const updatedLeg = detailsResponse.legs[detailsLegIndex]
    const nOrigLayovers = oldResponse.layovers.length

    oldResponse.legs[origLegIndex] = {
      ...oldResponse.legs[origLegIndex],
      layovers: increaseValues(updatedLeg.layovers, nOrigLayovers),
    }
  })
}

const updateHops = (oldResponse, detailsResponse, indexMap) => {
  Object.keys(indexMap.hops).forEach(detailsHopIndex => {
    const origHopIndex = indexMap.hops[detailsHopIndex]
    const updatedHop = detailsResponse.hops[detailsHopIndex]

    oldResponse.hops[origHopIndex] = {
      ...oldResponse.hops[origHopIndex],
      name: updatedHop.name,
      arrivalDate: updatedHop.arrivalDate,
      arrivalTime: updatedHop.arrivalTime,
      departureDate: updatedHop.departureDate,
      departureTime: updatedHop.departureTime,
      duration: updatedHop.duration,
    }
  })
}

const updateFares = (oldResponse, detailsResponse, indexMap) => {
  Object.keys(indexMap.fares).forEach(detailsFareIndex => {
    const origFareIndex = indexMap.fares[detailsFareIndex]
    const updatedFare = detailsResponse.fares[detailsFareIndex]

    oldResponse.fares[origFareIndex] = {
      ...oldResponse.fares[origFareIndex],
      price: updatedFare.price,
      fareBookingData: updatedFare.fareBookingData,
    }
  })
}

const increaseValues = (array, val) => array.map(x => x + val)

// Create a map from details indexes to response indexes
const createIndexMap = (response, detailsResponse, oItinIndex, iItinIndex) => {
  const indexMap = {
    outboundItineraries: {},
    inboundItineraries: {},
    legs: {},
    hops: {},
    fares: {},
  }

  if (oItinIndex != null) {
    indexMap.outboundItineraries[0] = oItinIndex
  }
  if (iItinIndex != null) {
    indexMap.inboundItineraries[0] = iItinIndex
  }

  // TODO: There's no sanity checking (make sure matching itineraries have the same number of legs
  // and matching legs have the same number of hops)
  if (response.outboundItineraries.length) {
    response.outboundItineraries[oItinIndex].legs.forEach((legIndex, i) => {
      indexMap.legs[detailsResponse.outboundItineraries[0].legs[i]] = legIndex
    })
  }
  if (response.inboundItineraries.length) {
    response.inboundItineraries[iItinIndex].legs.forEach((legIndex, i) => {
      indexMap.legs[detailsResponse.inboundItineraries[0].legs[i]] = legIndex
    })
  }


  Object.keys(indexMap.legs).forEach(legIndex => {
    const detailsHops = detailsResponse.legs[legIndex].hops
    const responseHops = response.legs[indexMap.legs[legIndex]].hops

    detailsHops.forEach((hopIndex, i) => {
      indexMap.hops[hopIndex] = responseHops[i]
    })
  })

  detailsResponse.fares.forEach((detailsFare, fareIndex) => {
    const fareMatches = response.fares.filter(f => compareFare(f, detailsFare, indexMap))
    const minFare = arrayMin(fareMatches, f => f.price, f => f)
    indexMap.fares[fareIndex] = response.fares.indexOf(minFare)
  })

  return indexMap
}

const compareFare = (origFare, detailsFare, indexMap) => {
  const legs = detailsFare.legs || []
  const legPairs = detailsFare.legPairs || []
  const mappedLegs = legs.map(l => indexMap.legs[l])
  const mappedLegPairs = legPairs.map(lp =>
    ({
      outboundLeg: indexMap.legs[lp.outboundLeg],
      inboundLeg: indexMap.legs[lp.inboundLeg],
    })
  )

  if (origFare.name !== detailsFare.name) {
    return false
  }

  if (origFare.agentName !== detailsFare.agentName) {
    return false
  }

  if (mappedLegs.length > 0) {
    const matchingLegs = intersect(origFare.legs, mappedLegs, (leg1, leg2) => leg1 === leg2)
    if (matchingLegs.length === 0) {
      return false
    }
  }

  if (mappedLegPairs.length > 0) {
    const matchingLegPairs = intersect(origFare.legPairs, mappedLegPairs,
      (lp1, lp2) => lp1.outboundLeg === lp2.outboundLeg && lp1.inboundLeg === lp2.inboundLeg)
    if (matchingLegPairs.length === 0) {
      return false
    }
  }

  return true
}

export default searchResponsesReducer
